"use client"

import { useQuery } from "@tanstack/react-query"
import { ShoppingBagIcon, TruckIcon } from "lucide-react"
import { getCartItems } from "@/api/cart"
import { getCheckoutOrderSummary } from "@/api/checkout"
import { CartCouponSection } from "@/components/account/cart/coupon"

const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  currency: "BRL",
  style: "currency",
})

const formatCurrency = (value: number): string => {
  return currencyFormatter.format(value)
}

export const OrderSummarySection = () => {
  const cartQuery = useQuery({
    queryKey: ["cart", "items"],
    queryFn: getCartItems,
    retry: false,
  })

  const checkoutShippingAddress = {
    country: cartQuery.data?.shippingAddress?.country ?? "BR",
    state: cartQuery.data?.shippingAddress?.state ?? "SP",
    city: cartQuery.data?.shippingAddress?.city ?? "",
    postcode: cartQuery.data?.shippingAddress?.postcode ?? "",
  }

  const orderReviewQuery = useQuery({
    queryKey: [
      "checkout",
      "order-review",
      checkoutShippingAddress.country,
      checkoutShippingAddress.state,
      checkoutShippingAddress.city,
      checkoutShippingAddress.postcode,
    ],
    queryFn: () => getCheckoutOrderSummary(checkoutShippingAddress),
    retry: false,
  })

  const products = cartQuery.data?.items ?? []
  const cartSubtotal = cartQuery.data?.totals.subtotal ?? 0
  const cartTotal = cartQuery.data?.totals.total ?? 0
  const discountTotal = cartQuery.data?.totals.discountTotal ?? 0

  if (products.length === 0) {
    return null
  }

  const subtotal = orderReviewQuery.data?.totals?.subtotal ?? cartSubtotal
  const total = orderReviewQuery.data?.totals?.total ?? cartTotal
  const shippingLabel =
    orderReviewQuery.data?.shippingMethods?.find(
      (method) => method.id === orderReviewQuery.data?.selectedShippingMethod
    )?.label ??
    orderReviewQuery.data?.shippingMethods?.[0]?.label ??
    "A calcular"

  return (
    <section className='space-y-4 rounded-2xl border border-zinc-200 bg-white p-5 shadow-xs sm:p-6'>
      <h2 className='font-semibold text-lg text-zinc-900 tracking-tight sm:text-xl'>
        Seu pedido
      </h2>

      {orderReviewQuery.isLoading ? (
        <p className='rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-600'>
          Sincronizando resumo do checkout...
        </p>
      ) : null}

      {orderReviewQuery.isError ? (
        <p className='rounded-xl border border-destructive/50 bg-destructive/5 px-3 py-2 text-destructive text-sm'>
          Nao foi possivel atualizar o resumo. Recarregue a pagina.
        </p>
      ) : null}

      {orderReviewQuery.data?.reload ? (
        <p className='rounded-xl border border-zinc-300 bg-zinc-50 px-3 py-2 text-sm text-zinc-700'>
          Sua sessao de checkout expirou. Volte ao carrinho para atualizar os
          dados.
        </p>
      ) : null}

      <div className='space-y-3 rounded-xl border border-zinc-200 bg-zinc-50/70 p-4'>
        {products.map((product) => (
          <div className='flex items-start gap-3' key={product.key}>
            <div className='mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-lg bg-white text-zinc-600 shadow-xs'>
              <ShoppingBagIcon aria-hidden='true' size={18} />
            </div>
            <div className='space-y-2'>
              <p className='font-medium text-sm text-zinc-900 leading-5'>
                {product.name}
              </p>
              <p className='text-sm text-zinc-600'>
                Lojista:{" "}
                <span className='font-medium text-zinc-900'>
                  {product.store}
                </span>
              </p>
              <p className='text-sm text-zinc-600'>Qtd: {product.quantity}</p>
            </div>
          </div>
        ))}
      </div>

      <dl className='space-y-3 border-zinc-200 border-y py-4 text-sm'>
        <div className='flex items-center justify-between'>
          <dt className='text-zinc-600'>Subtotal</dt>
          <dd className='font-medium text-zinc-900'>
            {formatCurrency(subtotal)}
          </dd>
        </div>
        <div className='flex items-center justify-between'>
          <dt className='text-zinc-600'>Desconto</dt>
          <dd className='font-medium text-zinc-900'>
            {discountTotal > 0
              ? `- ${formatCurrency(discountTotal)}`
              : formatCurrency(0)}
          </dd>
        </div>
        <div className='flex items-start justify-between gap-2'>
          <dt className='inline-flex items-center gap-1.5 text-zinc-600'>
            <TruckIcon aria-hidden='true' size={14} />
            Frete
          </dt>
          <dd className='text-right text-zinc-700'>{shippingLabel}</dd>
        </div>
      </dl>

      <div className='flex items-center justify-between'>
        <span className='font-semibold text-xl text-zinc-900'>Total</span>
        <span className='font-semibold text-2xl text-primary'>
          {formatCurrency(total)}
        </span>
      </div>

      <CartCouponSection className='rounded-xl border-zinc-200 bg-zinc-50/50 p-4 shadow-none' />
    </section>
  )
}
